import React from 'react';
import { styled, ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { darken, lighten, alpha } from "@mui/material";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import ChevronRight from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { getFieldTheme } from '../Bs4Theme/Bs4Theme';
import { v4 as uuidv4 } from 'uuid';

import Bs4MenuGear from './Bs4MenuGear';
import Bs4Kartoteka from '../Bs4Kartoteka';
import Bs4MainDispatch from '../Bs4MainDispatch';
import Bs4Icon from '../Bs4Components/Bs4Icon'
import { shallowEqual } from '../Bs4Components/Bs4Utils'
import { Bs4VerticalMenuDrawer, Bs4VerticalMenuPopper } from './Bs4VerticalMenu';

import { getBoxSx, SmallScreenContext } from '../Bs4Theme/Bs4UxTemplates';

export const MENU_TYPE_TOP = 'top';

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.default,     
  '&:hover': {    
    backgroundColor: darken(theme.palette.background.default, 0.07),     
  },
  '&:active': {
    backgroundColor: darken(theme.palette.background.default, 0.1), 
  },
  '&:focus': {
    backgroundColor: darken(theme.palette.background.default, 0.07), 
  },  
  "&.Mui-selected": {
    backgroundColor: darken(theme.palette.background.default, 0.1), 
    '&:hover': {    
      backgroundColor: darken(theme.palette.background.default, 0.3),     
    },
    '&:active': {
      backgroundColor: darken(theme.palette.background.default, 0.3), 
    },
    '&:focus': {
      backgroundColor: darken(theme.palette.background.default, 0.3), 
    },  
  }  
}));

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.default,     
  '&:hover': {    
    backgroundColor: darken(theme.palette.background.default, 0.07),     
  },
  '&:active': {
    backgroundColor: darken(theme.palette.background.default, 0.1), 
  },
  '&:focus': {
    backgroundColor: darken(theme.palette.background.default, 0.07), 
  },  
  "&.Mui-selected": {
    backgroundColor: darken(theme.palette.background.default, 0.1), 
    '&:hover': {    
      backgroundColor: darken(theme.palette.background.default, 0.3),     
    },
    '&:active': {
      backgroundColor: darken(theme.palette.background.default, 0.3), 
    },
    '&:focus': {
      backgroundColor: darken(theme.palette.background.default, 0.3), 
    },  
  }  
}));

const ButtonGroupStyled = styled(ButtonGroup)(({ theme, selected }) => {
  if (selected) return ({
    color: theme.palette.primary.main,
    backgroundColor: darken(theme.palette.background.default, 0.1), 
    '&:hover': {    
      backgroundColor: darken(theme.palette.background.default, 0.3), 
      "& .MuiButtonGroup-grouped:not(:last-of-type)": {
        borderRightColor: darken(theme.palette.background.default, 0.4),
      }
    },
    '&:active': {
      backgroundColor: darken(theme.palette.background.default, 0.3), 
    },
    '&:focus': {
      backgroundColor: darken(theme.palette.background.default, 0.3), 
    },
    "& .MuiButtonGroup-grouped:not(:last-of-type)": {
      borderRightColor: darken(theme.palette.background.default, 0.4),
    }
  })
  return ({
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.default,
    '&:hover': {    
      backgroundColor: darken(theme.palette.background.default, 0.1), 
      "& .MuiButtonGroup-grouped:not(:last-of-type)": {
        borderRightColor: darken(theme.palette.background.default, 0.2),
      }
    },
    '&:active': {
      backgroundColor: darken(theme.palette.background.default, 0.1), 
    },
    '&:focus': {
      backgroundColor: darken(theme.palette.background.default, 0.1), 
    },
    "& .MuiButtonGroup-grouped:not(:last-of-type)": {
      borderRightColor: darken(theme.palette.background.default, 0.2),
    }
  })
});

const ButtonStyled = styled(Button)(({ theme }) => ({    
  color: 'inherit',
  backgroundColor: 'inherit',   
  '&:hover': {    
    color: 'inherit',
    backgroundColor: 'inherit',
  },
  '&:active': {
    color: 'inherit',
    backgroundColor: 'inherit',
  },
  '&:focus': {
    color: 'inherit',
    backgroundColor: 'inherit',
  },
}));

const ButtonStyled0 = styled(Button)(({ theme }) => ({    
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.default,   
  '&:hover': {    
    backgroundColor: darken(theme.palette.background.default, 0.2),    
  },
  '&:active': {
    backgroundColor: darken(theme.palette.background.default, 0.2), 
  },
  '&:focus': {
    backgroundColor: darken(theme.palette.background.default, 0.2), 
  },  
}));

const getItems = (parent, items, level) => {  
  if (!Array.isArray(items)) return null;
  return items.map(item => {
    item.parent = parent;
    const items = item.items;
    // // TEST >>> >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    //   if (item.bs4Id === 600 && items) {
    //     const nitem = JSON.parse(JSON.stringify(getItemMenu(930, parent.items)));
    //     delete item.xtype;
    //     // console.log('...mmmmmm...', item, items, nitem)
    //     items.push(nitem);
    //   }
    // // TEST <<<
    if (items) item.items = getItems(item, items, level + 1);
    item.key = uuidv4();    
    return item;
  })
}

const closParenteItems = (parent) => {
  if (!parent) return;
  for (const item of parent.items || []) {
    closeAllItems(item);
  }
}

const closeAllItems = (item) => {
  if (!item) return;
  if (item.setOpen) item.setOpen(false);  
  for (const cIitem of item.items || []) {
    closeAllItems(cIitem);
  }
}

const openParents = (item) => {
  if (!item?.parent) return;
  item.parent.open = true;
  if (item.parent.setOpen) item.parent.setOpen(true);
  openParents(item.parent);
}

const getTopMenu = (item) => {
  if (!item) return null;
  while (item.parent) item = item.parent;
  return item;
}

const isEqualId = (id, item) => {    
  if (item.bs4Id && String(item.bs4Id) === String(id)) return true;
  if (item.bs4FolderId && String(item.bs4FolderId) === String(id)) return true;
  return false
}

const ItemMenuList = (props) => {
  return (    
    <MenuList 
      // autoFocus 
      // autoFocusItem
    >        
      {props.item.items.map(itemChild => 
        <ItemMenu 
          key={itemChild.key} 
          item={itemChild} 
          level={props.level} 
          onMenuClick={props.onMenuClick} 
          delayedTabsData={props.delayedTabsData} 
          bs4ConstructorMode={props.bs4ConstructorMode}
          bs4KartotekaId={props.bs4KartotekaId}
          targetOption={props.targetOption}
          showTooltip={props.showTooltip}
          focus={props.focus}
          optionId={props.optionId}
        />)}
    </MenuList>    
  )
}

const PopperMenu = (props) => {
  if (!props.item.items) return null;

  const handleMouseLeave = (event) => {
    if (props.showTooltip) return;
    closeAllItems(props.item)
    props.setOpen(false)
  }

  return (      
    <Popper
      keepMounted
      sx={{zIndex: props.theme.zIndex.modal}}
      //disablePortal
      open={props.open}
      anchorEl={props.refItem.current}
      placement={props.placement}
      // modifiers={{
      //   // preventOverflow: {
      //   //   enabled: false,
      //   //   boundariesElement: 'window',
      //   //   escapeWithReference: true
      //   // }
      // }}     
      transition
      // onMouseLeave={handleMouseLeave}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={400}>
          <Paper 
            elevation={5}            
            sx={{
              // flex: 1, 
              // overflow: 'hidden', 
              overflow: 'auto', 
              p: 0.5, 
              //borderRadius: theme.Bs4Menu.borderRadius, 
              //backgroundColor: theme.palette.primary.main
              maxHeight: '60vh',
            }}
          >
            <ItemMenuList 
              item={props.item} 
              level={props.level} 
              onMenuClick={props.onMenuClick} 
              delayedTabsData={props.delayedTabsData} 
              bs4ConstructorMode={props.bs4ConstructorMode}
              bs4KartotekaId={props.bs4KartotekaId}
              targetOption={props.targetOption}
              showTooltip={props.showTooltip}    
              focus={props.focus}
              optionId={props.optionId}
            />
          </Paper>
        </Fade>
      )}
    </Popper>      
  )
}

const hesItemOptionId = (topItem, optionId) => {
  if (parseInt(topItem.bs4Id) === parseInt(optionId)) return true;
  if (Array.isArray(topItem.items)) for (const item of topItem.items) {
    if (hesItemOptionId(item, optionId)) return true;
  }

  return false;
};

const ItemMenu = (props) => {
  const {item, level, onMenuClick, delayedTabsData, showTooltip} = props;
  const [open, setOpen] = React.useState(false);
  const selected = React.useMemo(() => {
    return hesItemOptionId(item, props.optionId);
  }, [props.optionId, item])

  item.setOpen = setOpen;
  
  // if (item?.bs4CustomCss && props.top) {
  //   const sx = JSON.parse(item.bs4CustomCss);
  //   sx.backgroundColor = '#ff0000';
  //   item.bs4CustomCss = JSON.stringify(sx);
  // }

  const mainTheme = useTheme();  
  const theme = React.useMemo(() => createTheme(getFieldTheme(mainTheme, item?.bs4CustomCss)), [mainTheme])

  const refItemMenu = React.useRef();

  const boxSx = React.useMemo(() => {
    try {
      const sx = JSON.parse(item.bs4CustomCss);      
      delete sx.color;
      delete sx.backgroundColor;  
      const boxSx = getBoxSx(sx, theme);
      return boxSx
    } catch (error) {}
    return null;
  }, [item.bs4CustomCss, theme]);

    
  const debug = false;
  const getItemTitle = () => {
    let title = (item.text ? item.text : '') //+ ' ' + item.bs4Id;
    if (debug) {
      const typ = item.bs4Id ? 'I' : item.bs4FolderId ? 'F' : '?'
      title = typ+'_'+(item.bs4Id || item.bs4FolderId || '???')+':'+title;
    }
    const string = delayedTabsData?.[item?.bs4Id];
    if (string) return title+' '+string;
    return title;
  }

  const handleCickButton = (e) => {
    if (Bs4MainDispatch.isCommandParserWaitForServer()) return;
    onMenuClick(item)
    closeAllItems(getTopMenu(item));
  }

  const handleMenuClick = (e) => {              
    // console.log('...MenuClick.0.', item, Boolean(!item.items))
    if (!item.items) {
      // console.log('...MenuClick.1.', item);
      if (Bs4MainDispatch.isCommandParserWaitForServer()) return;
      onMenuClick(item);      
      closeAllItems(getTopMenu(item));
      // setOpen(false);
    } else {
      // console.log('...MenuClick.2.', item, open);
      setOpen(true);
    }
    if (showTooltip?.closeTooltip) {
      showTooltip.setShowTooltip(null);
      showTooltip.closeTooltip();
    }
  }

  const handleMouseEnter = (e) => {
    if (props.showTooltip) return;
    closParenteItems(item.parent);
    setOpen(true)
  }

  React.useEffect(() => {
    if (isEqualId(showTooltip?.id, item)) {
      const close = (closeMenu) => { 
        showTooltip?.setShowTooltip(null);
        // 2025-04-23 ??? if (closeMenu) return;
        closeAllItems(getTopMenu(item));
        showTooltip.setFocus(false);        
      }
      
      showTooltip?.showTooltip(refItemMenu, close);
    }

    return () => {
      if (isEqualId(showTooltip?.id, item)) {
        // console.log('...???...', props)
      }
    }
  }, [item, showTooltip, refItemMenu.current]);

  if (level > 0) {
    if (item?.text?.startsWith('------')) return (
      <Divider/>
    )
    else return (      
      <ThemeProvider theme={theme}>
        <MenuItemStyled
          ref={refItemMenu}
          dense
          //divider
          onClick={handleMenuClick}
          onMouseEnter={handleMouseEnter}
          sx={boxSx}
          selected={selected}
        >
          {/* <ListItemIcon>{item?.iconCls && <i className={item.iconCls}/>} </ListItemIcon> */}
          <ListItemIcon><Bs4Icon iconCls={item.iconCls} 
          // iconStyle={item.iconStyle}
          /></ListItemIcon>          
          <ListItemText>{getItemTitle()}</ListItemText>
          {item.items && <Typography variant="body2" color="text.secondary"><ChevronRight/></Typography>}
          <Bs4MenuGear
            item={item} 
            bs4ConstructorMode={props.bs4ConstructorMode}
            bs4KartotekaId={props.bs4KartotekaId}
            targetOption={props.targetOption}
          />
        </MenuItemStyled>
        <PopperMenu 
          open={open && props.focus}
          theme={theme}
          refItem={refItemMenu}
          placement='right-start' 
          item={item} 
          level={level+1} 
          onMenuClick={onMenuClick} 
          delayedTabsData={delayedTabsData} 
          bs4ConstructorMode={props.bs4ConstructorMode}
          bs4KartotekaId={props.bs4KartotekaId}
          targetOption={props.targetOption}
          showTooltip={props.showTooltip}
          setOpen={setOpen}
          focus={props.focus}
          optionId={props.optionId}
        />
      </ThemeProvider>
    )
  } else if (item.xtype == 'splitbutton') { 
    return (
      <ThemeProvider theme={theme}>
        <ButtonGroupStyled ref={refItemMenu} sx={boxSx} selected={selected}>
          <ButtonStyled onClick={handleCickButton} onMouseEnter={handleMouseEnter} sx={boxSx}>
            <Bs4Icon iconCls={item.iconCls} iconStyle={item.iconStyle}/>
            {item?.iconCls && getItemTitle() && <span>&nbsp;</span>}
            {getItemTitle()}
            <Bs4MenuGear
              item={item} 
              bs4ConstructorMode={props.bs4ConstructorMode} 
              bs4KartotekaId={props.bs4KartotekaId}
              targetOption={props.targetOption}
            />
          </ButtonStyled>          
          <ButtonStyled onClick={handleMenuClick} onMouseEnter={handleMouseEnter} sx={boxSx}>
            <KeyboardArrowDownIcon/>            
          </ButtonStyled>          
        </ButtonGroupStyled>
        <PopperMenu 
            open={open && props.focus}
            theme={theme}
            refItem={refItemMenu}
            placement='bottom-start' 
            item={item} 
            level={level+1} 
            onMenuClick={onMenuClick} 
            delayedTabsData={delayedTabsData} 
            bs4ConstructorMode={props.bs4ConstructorMode}
            bs4KartotekaId={props.bs4KartotekaId}
            targetOption={props.targetOption}
            showTooltip={props.showTooltip}
            setOpen={setOpen}
            focus={props.focus}            
            optionId={props.optionId}
          />
      </ThemeProvider>
    )
  } else if (Array.isArray(item.items) && item.items.length > 0) {          
    return (        
      <ThemeProvider theme={theme}>
        {props.IconButton ? 
          <IconButtonStyled 
            ref={refItemMenu} 
            onClick={handleMenuClick} 
            onMouseEnter={handleMouseEnter} 
            sx={boxSx}
          >
            {props.startIcon ? <props.startIcon/> : <KeyboardArrowDownIcon/>}
          </IconButtonStyled>
        : 
          <ButtonGroupStyled ref={refItemMenu} sx={boxSx} selected={selected}>
            <ButtonStyled onClick={handleMenuClick} onMouseEnter={handleMouseEnter} sx={boxSx}>
              <Bs4Icon iconCls={item.iconCls} iconStyle={item.iconStyle}/>
              {item?.iconCls && getItemTitle() && <span>&nbsp;</span>}
              {getItemTitle()}
              {/* <Bs4MenuGear
                item={item} 
                bs4ConstructorMode={props.bs4ConstructorMode} 
                bs4KartotekaId={props.bs4KartotekaId}
                targetOption={props.targetOption}
              /> */}
              <KeyboardArrowDownIcon/>              
            </ButtonStyled>
          </ButtonGroupStyled>
        }
                
        <PopperMenu 
          open={open && props.focus}
          theme={theme}
          refItem={refItemMenu}
          placement='bottom-start' 
          item={item} 
          level={level+1} 
          onMenuClick={onMenuClick} 
          delayedTabsData={delayedTabsData} 
          bs4ConstructorMode={props.bs4ConstructorMode}
          bs4KartotekaId={props.bs4KartotekaId}
          targetOption={props.targetOption}
          showTooltip={props.showTooltip}
          setOpen={setOpen}
          focus={props.focus}
          optionId={props.optionId}
        />
      </ThemeProvider>
    )
  } else {
    return (
      <ThemeProvider theme={theme}>
        <ButtonGroupStyled ref={refItemMenu} sx={boxSx} selected={selected}>
          <ButtonStyled onClick={handleCickButton} onMouseEnter={handleMouseEnter} sx={boxSx}>
            <Bs4Icon iconCls={item.iconCls} iconStyle={item.iconStyle}/>
            {item?.iconCls && getItemTitle() && <span>&nbsp;</span>}
            {getItemTitle()}
            <Bs4MenuGear
              item={item} 
              bs4ConstructorMode={props.bs4ConstructorMode} 
              bs4KartotekaId={props.bs4KartotekaId}
              targetOption={props.targetOption}
            />
          </ButtonStyled>
        </ButtonGroupStyled>
      </ThemeProvider>
    )
  }    
}

const getItemMenu = (id, items) => {
  if (!items) return null;
  for (const item of items) {
    if (String(item.bs4Id) === String(id) || String(item.bsFolder4Id) === String(id)) return item;
    const returnedItem = getItemMenu(id, item.items);
    if (returnedItem) return returnedItem;
  }
}

const Bs4HorizontalMenuContent = React.memo((props) => {
  const [focus, setFocus] = React.useState(false);  
  const [delayedTabsData, setDelayedTabsData] = React.useState({});
  const [showTooltip, setShowTooltip] = React.useState(null);
  const [optionId, setOptionId] = React.useState(null);  
  const refFocusIn = React.useRef(null);
  const menu = React.useMemo(() => {
    const menu = {
      parent: null, 
    };
    menu.canShowTooltip = id => getItemMenu(id, menu.items);
    menu.showTooltip = (id, showTooltip, closeTooltip) => {
      const item = getItemMenu(id, menu.items);
      openParents(item)
      setFocus(true);
      setShowTooltip({
        id,
        showTooltip,
        closeTooltip,
        setShowTooltip,        
        setFocus,
      });
    };    
    menu.items = JSON.stringify(props?.bs4Menu);
    menu.items = menu.items ? JSON.parse(menu.items) : [];
    menu.items = getItems(menu, menu.items, 0) || [];
    menu.setOptionId = setOptionId;
    if (props.setMenu) props.setMenu(menu);
    return menu;
  }, [props?.bs4Menu, setFocus, setShowTooltip])  

  const bs4ConstructorMode = React.useMemo(() => {
    const bs4Kartoteka = Bs4Kartoteka.get(Bs4Kartoteka.firstBs4KartotekaId);
    return bs4Kartoteka?.message?._Komunikat?.bs4ConstructorMode;
  }, []);
  
  const handleFocusBlur = React.useCallback((event) => {
    const focus = event.type === 'focus';
    // console.log('...???...MENU focus:', focus, event.type)
    setFocus(focus);
    if (!focus) closeAllItems(menu);
  }, [setFocus, closeAllItems]);

  React.useEffect(() => {
    if (menu.items.length < 1) return;

    if (props.addDelayedTabsData) {
      const bs4Kartoteka = Bs4Kartoteka.get(props.bs4KartotekaId);
      if (bs4Kartoteka) {
        bs4Kartoteka.addDelayedTabsData = (message) => {         
          Bs4MainDispatch.speedRun((tag) => {
            console.log(Bs4MainDispatch.speedNextTime(tag), Bs4MainDispatch.speedBs4KarOpt(props.bs4KartotekaId, props.optionId))
          }, '..MenuH..Delayed');
          setDelayedTabsData(message._Komunikat)
        };
        Bs4MainDispatch.commandParser('Bs4LoadCounter', {currCardViewId: bs4Kartoteka.bs4KartotekaId}, bs4Kartoteka.bs4KartotekaId);
      }
    }    

    refFocusIn.current.blur();
    refFocusIn.current.focus();
  }, [])

  const spacing = '6px';

  // console.log('...???...Bs4HorizontalMenu...', focus, menu)
  const refSpeedTest = React.useRef({
    shallowEqualValues: null,
    shallowEqualProps: null,
  });
  Bs4MainDispatch.speedRun((tag) => {
    const newObj = {
      focus,
      delayedTabsData,
      showTooltip,
      optionId,
      refFocusIn,
      menu,      
    };
    console.log(Bs4MainDispatch.speedNextTime(tag), props.debugText || '', shallowEqual(newObj, refSpeedTest.current.shallowEqualValues, Bs4MainDispatch.speedPrefix+tag+' EqualValues'), shallowEqual(props, refSpeedTest.current.shallowEqualProps, Bs4MainDispatch.speedPrefix+tag+' EqualProps'));
    refSpeedTest.current.shallowEqualValues = newObj;
    refSpeedTest.current.shallowEqualProps = props;
  }, '..MenuH..Content');        

  return (    
    <Box 
      ref={refFocusIn} 
      display="flex" 
      flexDirection="row" 
      flexWrap='wrap' 
      gap={spacing} 
      paddingY={spacing}
      // tabIndex={0}
      onFocus={handleFocusBlur}      
      onBlur={handleFocusBlur}
      // onMouseLeave={handleMouseLeave}
    >      
      {menu.items.map(itemChild => (
        <ItemMenu
          top={true}
          key={itemChild.key} 
          item={itemChild} 
          level={0} 
          onMenuClick={props.onMenuClick}
          delayedTabsData={delayedTabsData}
          bs4ConstructorMode={bs4ConstructorMode}
          bs4KartotekaId={props.bs4KartotekaId}
          targetOption={props.targetOption}
          showTooltip={showTooltip}
          focus={focus}
          startIcon={props.startIcon}
          IconButton={props.IconButton}
          optionId={optionId}
        />
      ))}
    </Box>
  )
});

export default function Bs4HorizontalMenu (props) {
  const smallScreen = React.useContext(SmallScreenContext);
  const [MenuIconButton, setMenuIconButton] = React.useState(null);

  const setMenuIconButtonProps = React.useCallback((MenuIconButton) => {
    if (props.setMenuIconButton) {
      props.setMenuIconButton(MenuIconButton);
      setMenuIconButton(null);
    } else {
      setMenuIconButton(MenuIconButton);
    }
  }, [props.setMenuIconButton, setMenuIconButton]);

  if (! Array.isArray(props?.bs4Menu) || props?.bs4Menu.length < 1) return null;
  if (props.bs4MenuType && props.bs4MenuType !== MENU_TYPE_TOP) return null;
  
  if (smallScreen) return (    
    <React.Fragment>
      {MenuIconButton}
      {props.variantMenuIcon === 'popper'
      ?
        <Bs4VerticalMenuPopper {...props} anchor='right' setMenuIconButton={setMenuIconButtonProps}/>
      :
        <Bs4VerticalMenuDrawer {...props} anchor='right' setMenuIconButton={setMenuIconButtonProps}/>
      }
    </React.Fragment>    
  );

  return (
    <Bs4HorizontalMenuContent {...props} />
  );
}